import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/VisibilityOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
  createChannelPartner,
  createChannelPartnerLead,
  listChannelPartnerLeads,
  listChannelPartners,
} from "../../services/channelPartnerService";
import { listTenantOrganizations } from "../../services/organizationService";
import { listGrades } from "../../services/gradeService";
import { getProjectDetails } from "../Project/components/ShowAll/ShowAllService";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectRole1,
} from "../../redux/authSlice";
import urls from "../../global/constants/UrlConstants";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhSelect, { buildStageSelectGroups } from "../../ui/YhSelect";
import YhMultiSelect from "../../ui/YhMultiSelect";
import YhModal from "../../ui/YhModal";
import PhoneWithCountryCode, {
  composePhoneWithCountry,
  defaultPhoneCountry,
  findPhoneCountry,
  formatPhoneKeepingCountryCode,
  nationalPhoneDigits,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";
import { ReactComponent as VerticalDotIcon } from "../../assets/icons/verticalDot.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import ChannelPartnerCallTray from "./ChannelPartnerCallTray";

const emptyForm = () => ({
  firmName: "",
  phone: "",
  phoneCountryCode: defaultPhoneCountry().code,
  email: "",
  grade: "",
  channelNameCount: "",
  phoneMaskingEnabled: false,
  organizationIds: [] as string[],
  projectIds: [] as string[],
});

const emptyLeadForm = () => ({
  leadName: "",
  leadPhone: "",
  phoneCountryCode: defaultPhoneCountry().code,
  leadEmail: "",
  organizationIds: [] as string[],
  projectIds: [] as string[],
  stage: "",
});

function firstStageValue(
  groups: { options: { value: string | number }[] }[]
) {
  for (const g of groups || []) {
    if (g?.options?.[0]?.value != null && g.options[0].value !== "") {
      return String(g.options[0].value);
    }
  }
  return "Lead";
}

const ChannelPartnersPage: React.FC = () => {
  const history = useHistory();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const hierarchyLevel = useSelector(selectRole1);
  const canSelectOrganization =
    isSuperAdmin || String(hierarchyLevel) === "1";

  const lockedOrgId =
    !canSelectOrganization &&
    activeOrgId &&
    activeOrgId !== "all" &&
    activeOrgId !== "*"
      ? String(activeOrgId)
      : "";

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState(lockedOrgId || "");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [grades, setGrades] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSaving, setLeadSaving] = useState(false);
  const [leadForm, setLeadForm] = useState(emptyLeadForm());
  const [leadCp, setLeadCp] = useState<any | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<any | null>(null);
  const [stageData, setStageData] = useState<any[]>([]);
  const stageGroups = useMemo(
    () => buildStageSelectGroups(stageData || []),
    [stageData]
  );

  const [expandedId, setExpandedId] = useState<string>("");
  const [expandedLeads, setExpandedLeads] = useState<any[]>([]);
  const [expandedTotal, setExpandedTotal] = useState(0);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [menuRow, setMenuRow] = useState<any | null>(null);

  const selectedOrgIds = useMemo(() => {
    if (lockedOrgId) return [lockedOrgId];
    return form.organizationIds || [];
  }, [lockedOrgId, form.organizationIds]);

  const orgOptions = useMemo(
    () =>
      orgs.map((o) => ({
        label: o.brandName || o.name,
        value: String(o._id),
      })),
    [orgs]
  );

  const gradeOptions = useMemo(
    () => [
      { label: "Select grade…", value: "" },
      ...grades.map((g) => ({
        label: g.name,
        value: g.name,
      })),
    ],
    [grades]
  );

  const projectOptions = useMemo(() => {
    const orgSet = new Set(selectedOrgIds.map(String).filter(Boolean));
    return (projects || [])
      .filter((p: any) => {
        if (!orgSet.size) return false;
        const oid = String(p.organizationId || "");
        if (!oid) return true;
        return orgSet.has(oid);
      })
      .map((p: any) => ({
        label: p.project_name || p.name || p.project_uid || String(p._id),
        value: String(p._id),
      }));
  }, [projects, selectedOrgIds]);

  const loadOrgs = useCallback(async () => {
    if (!canSelectOrganization) return;
    try {
      const res = await listTenantOrganizations();
      const list = res?.data?.items || res?.data || res?.items || [];
      setOrgs(Array.isArray(list) ? list : []);
    } catch (_) {
      setOrgs([]);
    }
  }, [canSelectOrganization]);

  const loadGrades = useCallback(async () => {
    try {
      const res = await listGrades();
      const data = res?.data || res || [];
      setGrades(Array.isArray(data) ? data : []);
    } catch (_) {
      setGrades([]);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const res = await getProjectDetails({
        pageNumber: 0,
        pageSize: 500,
        search: "",
        status: "1",
        propertyPurpose: "",
      });
      setProjects(Array.isArray(res?.data) ? res.data : []);
    } catch (_) {
      setProjects([]);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const headerOrg =
        activeOrgId && activeOrgId !== "all" && activeOrgId !== "*"
          ? String(activeOrgId)
          : "";
      const effectiveOrg =
        lockedOrgId || orgFilter || headerOrg || undefined;
      const res = await listChannelPartners({
        organizationId: effectiveOrg,
        search: search || undefined,
        page,
        limit: pageSize,
      });
      const data = res?.data || res;
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load Channel Partners");
    } finally {
      setLoading(false);
    }
  }, [orgFilter, search, lockedOrgId, page, pageSize, activeOrgId]);

  // Keep page filter aligned with header organization switcher
  useEffect(() => {
    if (lockedOrgId) {
      setOrgFilter(lockedOrgId);
      return;
    }
    if (activeOrgId && activeOrgId !== "all" && activeOrgId !== "*") {
      setOrgFilter(String(activeOrgId));
      return;
    }
    if (activeOrgId === "all" || activeOrgId === "*") {
      setOrgFilter("");
    }
  }, [activeOrgId, lockedOrgId]);

  useEffect(() => {
    loadOrgs();
    loadGrades();
    loadProjects();
  }, [loadOrgs, loadGrades, loadProjects]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getStage();
        setStageData(Array.isArray(res?.data) ? res.data : []);
      } catch (_) {
        setStageData([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (lockedOrgId) setOrgFilter(lockedOrgId);
  }, [lockedOrgId]);

  useEffect(() => {
    setPage(1);
  }, [search, orgFilter, lockedOrgId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const totalPages = Math.max(
    1,
    Math.ceil((Number(total) || 0) / pageSize) || 1
  );
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  useEffect(() => {
    const allowed = new Set(projectOptions.map((p) => String(p.value)));
    setForm((f) => {
      const next = (f.projectIds || []).filter((id) => allowed.has(String(id)));
      if (next.length === (f.projectIds || []).length) return f;
      return { ...f, projectIds: next };
    });
  }, [projectOptions]);

  const orgNames = (row: any) => {
    const mems = row.memberships || [];
    if (!mems.length) return "—";
    return mems
      .map((m: any) => m.organizationName || "—")
      .filter(Boolean)
      .join(", ");
  };

  const orgNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    orgs.forEach((o) => {
      map[String(o._id)] = o.brandName || o.name || String(o._id);
    });
    return map;
  }, [orgs]);

  const openAdd = () => {
    setForm({
      ...emptyForm(),
      organizationIds: lockedOrgId
        ? [lockedOrgId]
        : orgFilter
          ? [orgFilter]
          : [],
      projectIds: [],
    });
    setAddOpen(true);
  };

  const handleCreate = async () => {
    const national = nationalPhoneDigits(form.phone, 15);
    if (!form.firmName.trim() || !national) {
      openErrorNotification("Firm name and phone are required");
      return;
    }
    const organizationIds = lockedOrgId
      ? [lockedOrgId]
      : form.organizationIds;
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization");
      return;
    }
    const country =
      findPhoneCountry(form.phoneCountryCode) || defaultPhoneCountry();
    const composed = composePhoneWithCountry(country.dialCode, national);
    try {
      setSaving(true);
      await createChannelPartner({
        firmName: form.firmName.trim(),
        phone: composed,
        phoneDialCode: country.dialCode,
        phoneCountryCode: country.code,
        email: form.email.trim() || undefined,
        grade: form.grade || undefined,
        channelNameCount: Number(form.channelNameCount) || 0,
        phoneMaskingEnabled: !!form.phoneMaskingEnabled,
        organizationIds,
        projectIds: form.projectIds || [],
      });
      openSuccessNotification("Channel Partner created");
      setAddOpen(false);
      setForm(emptyForm());
      loadList();
    } catch (e: any) {
      openErrorNotification(e?.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const openAddLead = (row: any) => {
    const mems = row.memberships || [];
    const defaultOrgs = lockedOrgId
      ? [lockedOrgId]
      : orgFilter
        ? [orgFilter]
        : mems.length === 1
          ? [String(mems[0].organizationId)]
          : [];
    setLeadCp(row);
    setLeadForm({
      ...emptyLeadForm(),
      organizationIds: defaultOrgs,
      projectIds: [],
      stage: firstStageValue(stageGroups),
    });
    setLeadOpen(true);
  };

  const leadOrgOptions = useMemo(() => {
    const mems = leadCp?.memberships || [];
    if (mems.length) {
      return mems.map((m: any) => ({
        label:
          m.organizationName ||
          orgNameMap[String(m.organizationId)] ||
          String(m.organizationId),
        value: String(m.organizationId),
      }));
    }
    return orgOptions;
  }, [leadCp, orgOptions, orgNameMap]);

  const leadSelectedOrgIds = useMemo(() => {
    if (lockedOrgId) return [lockedOrgId];
    return leadForm.organizationIds || [];
  }, [lockedOrgId, leadForm.organizationIds]);

  const leadProjectOptions = useMemo(() => {
    const orgSet = new Set(leadSelectedOrgIds.map(String).filter(Boolean));
    const mems = leadCp?.memberships || [];
    return (projects || [])
      .filter((p: any) => {
        if (!orgSet.size) return false;
        const oid = String(p.organizationId || "");
        if (oid && !orgSet.has(oid)) return false;
        if (!mems.length) return true;
        const mem = mems.find((m: any) => String(m.organizationId) === oid);
        if (!mem) return !oid;
        const pids = Array.isArray(mem.projectIds) ? mem.projectIds : [];
        if (!pids.length) return true;
        return pids.map(String).includes(String(p._id));
      })
      .map((p: any) => ({
        label: p.project_name || p.name || p.project_uid || String(p._id),
        value: String(p._id),
      }));
  }, [projects, leadSelectedOrgIds, leadCp]);

  useEffect(() => {
    const allowed = new Set(leadProjectOptions.map((p) => String(p.value)));
    setLeadForm((f) => {
      const next = (f.projectIds || []).filter((id) => allowed.has(String(id)));
      if (next.length === (f.projectIds || []).length) return f;
      return { ...f, projectIds: next };
    });
  }, [leadProjectOptions]);

  const submitLead = async () => {
    if (!leadCp?._id) return;
    if (!leadForm.leadName.trim()) {
      openErrorNotification("Lead name is required");
      return;
    }
    const national = nationalPhoneDigits(leadForm.leadPhone, 15);
    if (
      !(
        national.length === 4 ||
        (national.length >= 7 && national.length <= 15)
      )
    ) {
      openErrorNotification(
        "Enter a valid mobile (10 digits) or last 4 digits"
      );
      return;
    }
    if (!leadForm.stage) {
      openErrorNotification("Select a stage");
      return;
    }
    const organizationIds = lockedOrgId
      ? [lockedOrgId]
      : leadForm.organizationIds || [];
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization");
      return;
    }
    const country =
      findPhoneCountry(leadForm.phoneCountryCode) || defaultPhoneCountry();
    const composed = composePhoneWithCountry(country.dialCode, national);
    setLeadSaving(true);
    try {
      const res = await createChannelPartnerLead(String(leadCp._id), {
        leadName: leadForm.leadName.trim(),
        leadPhone: composed,
        phoneDialCode: country.dialCode,
        phoneCountryCode: country.code,
        leadEmail: leadForm.leadEmail.trim() || undefined,
        organizationIds,
        projectIds: leadForm.projectIds || [],
        stage: leadForm.stage,
      });
      if (res?.success === 0 || !res?.data) {
        openErrorNotification(res?.message || "Failed to add lead");
        return;
      }
      const referenced = Number(res?.data?.referencedCount || 0);
      const count = Number(res?.data?.createdCount || 1);
      openSuccessNotification(
        referenced > 0 && referenced >= count
          ? "Existing lead linked to this Channel Partner (reference)."
          : referenced > 0
            ? `Lead saved — ${referenced} existing lead(s) linked as reference.`
            : count > 1
              ? `Lead added for ${count} organizations.`
              : "Lead added for Channel Partner."
      );
      if (res?.data?.errors?.length) {
        openErrorNotification(res.data.errors.join("; "));
      }
      setLeadOpen(false);
      setLeadCp(null);
      setLeadForm(emptyLeadForm());
      await loadList();
      if (expandedId === String(leadCp._id)) {
        await loadExpandedLeads(String(leadCp._id));
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to add lead");
    } finally {
      setLeadSaving(false);
    }
  };

  const loadExpandedLeads = async (cpId: string) => {
    setLeadsLoading(true);
    try {
      const res = await listChannelPartnerLeads(cpId, {
        page: 1,
        limit: 20,
        organizationId: lockedOrgId || orgFilter || undefined,
      });
      const ld = res?.data || res;
      setExpandedLeads(ld?.items || []);
      setExpandedTotal(ld?.total || 0);
    } catch (e: any) {
      setExpandedLeads([]);
      setExpandedTotal(0);
      openErrorNotification(e?.message || "Failed to load leads");
    } finally {
      setLeadsLoading(false);
    }
  };

  const toggleLeads = async (row: any) => {
    const id = String(row._id);
    if (expandedId === id) {
      setExpandedId("");
      setExpandedLeads([]);
      setExpandedTotal(0);
      return;
    }
    setExpandedId(id);
    await loadExpandedLeads(id);
  };

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] w-full min-w-0 overflow-hidden bg-white">
      <div
        className={`box-border h-full min-h-0 min-w-0 overflow-auto px-4 py-4 transition-[flex-basis,width] duration-300 ease-in-out md:px-6 md:py-5 ${
          selectedPartner
            ? "w-full flex-[1_1_0%] pr-0 md:pr-3"
            : "w-full flex-[1_1_100%]"
        }`}
      >
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            Partners
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage partners, add leads, and track referrals. Phone visibility
            follows Organization Lead phone masking settings.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.metaGradeListPath)}
          >
            Manage Grades
          </YhButton>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.cpBulkUploadPath)}
          >
            Bulk Upload
          </YhButton>
          <YhButton variant="primary" size="sm" onClick={openAdd}>
            Add Partner
          </YhButton>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <YhInput
          label="Search"
          value={search}
          onChange={(e: any) => setSearch(e.target?.value ?? e)}
          placeholder="Firm, phone, email"
        />
        {canSelectOrganization ? (
          <YhSelect
            label="Organization"
            value={orgFilter}
            onChange={(e: any) =>
              setOrgFilter(String(e?.target?.value || ""))
            }
            options={[
              { label: "All organizations", value: "" },
              ...orgOptions,
            ]}
          />
        ) : null}
        <div className="flex items-end">
          <YhButton variant="secondary" size="sm" onClick={loadList}>
            Refresh
          </YhButton>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm text-[#6B7280]">
        <span>
          Showing{" "}
          <span className="font-semibold text-[#111827]">{from}</span>–
          <span className="font-semibold text-[#111827]">{to}</span> of{" "}
          <span className="font-semibold text-[#111827]">
            {(total || 0).toLocaleString()}
          </span>
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs">
            Rows
            <select
              className="rounded-lg border border-[#E5E7EB] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#A02448]"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#374151] disabled:opacity-40"
            >
              Prev
            </button>
            <span className="min-w-[3.5rem] text-center text-xs font-semibold text-[#111827]">
              {page}/{totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#374151] disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1080px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[#EEF0F5] bg-[#F9FAFB] text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                <th className="px-4 py-3">Firm</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Grade</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3 text-center">Leads</th>
                <th className="px-4 py-3">Masking</th>
                <th className="px-4 py-3">Status</th>
                <th className="sticky right-0 z-10 bg-[#F9FAFB] px-4 py-3 text-right shadow-[-8px_0_12px_rgba(15,23,42,0.04)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">
                    No channel partners yet.
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const id = String(row._id);
                  const open = expandedId === id;
                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`cursor-pointer border-b border-[#F3F4F6] hover:bg-[#FAFBFF] ${
                          selectedPartner &&
                          String(selectedPartner._id) === id
                            ? "bg-[#F9F0F4]"
                            : ""
                        }`}
                        onClick={() => setSelectedPartner(row)}
                      >
                        <td className="max-w-[220px] px-4 py-3">
                          <div className="truncate font-semibold text-[#111827]">
                            {row.firmName}
                          </div>
                          <div className="text-xs text-[#9CA3AF]">
                            {row.source || "—"}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-sm tracking-wide text-[#374151]">
                          {formatPhoneKeepingCountryCode(row.phone)}
                        </td>
                        <td className="max-w-[180px] truncate px-4 py-3 text-[#374151]">
                          {row.email || "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {row.grade || "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 capitalize text-[#374151]">
                          {row.sourceType === "manual"
                            ? "Manual"
                            : row.sourceType === "bulk"
                              ? "Bulk"
                              : "—"}
                        </td>
                        <td
                          className="max-w-[160px] truncate px-4 py-3 text-[#374151]"
                          title={orgNames(row)}
                        >
                          {orgNames(row)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLeads(row);
                            }}
                            className="inline-flex min-w-[2.5rem] items-center justify-center rounded-full bg-[#F9F0F4] px-2.5 py-1 text-xs font-bold text-[#4338CA] hover:bg-[#F9F0F4]"
                            title="Show leads"
                          >
                            {row.leadsCount || 0}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              row.phoneMaskingEnabled
                                ? "bg-[#F9F0F4] text-[#3730A3]"
                                : "bg-[#F3F4F6] text-[#6B7280]"
                            }`}
                          >
                            {row.phoneMaskingEnabled ? "On" : "Off"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-full bg-[#ECFDF5] px-2 py-0.5 text-xs font-semibold capitalize text-[#047857]">
                            {row.status || "active"}
                          </span>
                        </td>
                        <td className="sticky right-0 z-10 bg-white px-3 py-3 text-right shadow-[-8px_0_12px_rgba(15,23,42,0.04)]">
                          <Box>
                            <Button
                              className="action-button !min-w-0 !p-1.5"
                              data-action-button="true"
                              onClick={(event) => {
                                event.stopPropagation();
                                const rect =
                                  event.currentTarget.getBoundingClientRect();
                                setMenuRow(row);
                                setMenuPosition({
                                  top: rect.bottom,
                                  left: rect.left,
                                });
                              }}
                            >
                              <VerticalDotIcon />
                            </Button>
                          </Box>
                        </td>
                      </tr>
                      {open ? (
                        <tr className="border-b border-[#EEF0F5] bg-[#F8FAFC]">
                          <td colSpan={10} className="px-4 py-3">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-[#111827]">
                                Leads for {row.firmName}{" "}
                                <span className="font-normal text-[#6B7280]">
                                  ({expandedTotal})
                                </span>
                              </p>
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(
                                    `${urls.channelPartnersPath}/${row._id}`
                                  )
                                }
                                className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-semibold text-[#374151]"
                              >
                                Open dashboard
                              </button>
                            </div>
                            {leadsLoading ? (
                              <p className="text-sm text-[#6B7280]">
                                Loading leads…
                              </p>
                            ) : expandedLeads.length === 0 ? (
                              <p className="text-sm text-[#6B7280]">
                                No leads yet. Use Add lead to capture one.
                              </p>
                            ) : (
                              <div className="overflow-x-auto rounded-xl border border-[#E5E7EB] bg-white">
                                <table className="w-full min-w-[640px] text-left text-xs">
                                  <thead className="bg-[#F9FAFB] text-[#6B7280]">
                                    <tr>
                                      <th className="px-3 py-2 font-medium">
                                        Name
                                      </th>
                                      <th className="px-3 py-2 font-medium">
                                        Phone
                                      </th>
                                      <th className="px-3 py-2 font-medium">
                                        Email
                                      </th>
                                      <th className="px-3 py-2 font-medium">
                                        Stage
                                      </th>
                                      <th className="px-3 py-2 font-medium">
                                        Source
                                      </th>
                                      <th className="px-3 py-2 font-medium">
                                        Organization
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expandedLeads.map((l) => (
                                      <tr
                                        key={l._id}
                                        className="border-t border-[#F3F4F6]"
                                      >
                                        <td className="px-3 py-2 font-medium text-[#111827]">
                                          {l.leadName || "—"}
                                        </td>
                                        <td className="px-3 py-2 font-mono tracking-wide">
                                          {formatPhoneKeepingCountryCode(
                                            resolveLeadPhoneForDisplay(l)
                                          )}
                                        </td>
                                        <td className="px-3 py-2">
                                          {l.leadEmail || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                          {l.stage || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                          {l.source || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                          {orgNameMap[
                                            String(l.organizationId)
                                          ] ||
                                            String(l.organizationId || "—")}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Menu
        open={Boolean(menuPosition)}
        onClose={() => {
          setMenuPosition(null);
          setMenuRow(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          menuPosition
            ? { top: menuPosition.top, left: menuPosition.left - 160 }
            : undefined
        }
        PaperProps={{ sx: { width: 200, maxWidth: "100%" } }}
      >
        <MenuItem style={{ cursor: "default" }}>
          <ListItemText>Actions</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const id = menuRow?._id;
            setMenuPosition(null);
            setMenuRow(null);
            if (id) history.push(`${urls.channelPartnersPath}/${id}`);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = menuRow;
            setMenuPosition(null);
            setMenuRow(null);
            if (row) openAddLead(row);
          }}
        >
          <ListItemIcon>
            <PersonAddAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add lead</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = menuRow;
            setMenuPosition(null);
            setMenuRow(null);
            if (row) toggleLeads(row);
          }}
        >
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View leads</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Channel Partner */}
      <YhModal
        open={addOpen}
        onClose={() => !saving && setAddOpen(false)}
        title="Add Partner"
        width={520}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={saving}
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={saving}
              onClick={handleCreate}
            >
              {saving ? "Saving…" : "Save"}
            </YhButton>
          </div>
        }
      >
        <div className="space-y-3">
          <YhInput
            label="CP Firm Name *"
            value={form.firmName}
            onChange={(e: any) =>
              setForm((f) => ({ ...f, firmName: e.target?.value ?? e }))
            }
          />
          <PhoneWithCountryCode
            label="Phone Number"
            required
            countryCode={form.phoneCountryCode}
            phone={form.phone}
            phonePlaceholder="10-digit mobile"
            maxDigits={15}
            onCountryChange={(code) =>
              setForm((f) => ({ ...f, phoneCountryCode: code }))
            }
            onPhoneChange={(digits) =>
              setForm((f) => ({ ...f, phone: digits }))
            }
          />
          <YhInput
            label="Email"
            value={form.email}
            onChange={(e: any) =>
              setForm((f) => ({ ...f, email: e.target?.value ?? e }))
            }
          />
          <YhSelect
            label="Grade"
            value={form.grade}
            onChange={(e: any) =>
              setForm((f) => ({ ...f, grade: e.target?.value || "" }))
            }
            options={gradeOptions}
          />
          {canSelectOrganization ? (
            <YhMultiSelect
              label="Organizations"
              placeholder="Select organizations…"
              searchPlaceholder="Search organizations…"
              requiredMark
              showSelectAll
              maxVisibleChips={4}
              options={orgOptions}
              value={form.organizationIds}
              onChange={(values) =>
                setForm((f) => ({
                  ...f,
                  organizationIds: values.map(String),
                  projectIds: [],
                }))
              }
            />
          ) : (
            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-sm text-[#374151]">
              Organization is set from your login.
            </div>
          )}
          <YhMultiSelect
            label="Projects"
            placeholder={
              selectedOrgIds.length
                ? "Select projects…"
                : "Select organization first…"
            }
            searchPlaceholder="Search projects…"
            showSelectAll
            maxVisibleChips={4}
            disabled={!selectedOrgIds.length}
            options={projectOptions}
            value={form.projectIds}
            onChange={(values) =>
              setForm((f) => ({
                ...f,
                projectIds: values.map(String),
              }))
            }
          />
          <label className="flex items-center gap-2 text-sm text-[#374151]">
            <input
              type="checkbox"
              checked={form.phoneMaskingEnabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  phoneMaskingEnabled: e.target.checked,
                }))
              }
            />
            Enable phone masking for this Channel Partner
          </label>
        </div>
      </YhModal>

      {/* Add lead from list */}
      <YhModal
        open={leadOpen}
        onClose={() => !leadSaving && setLeadOpen(false)}
        title={`Add lead · ${leadCp?.firmName || "Channel Partner"}`}
        width={520}
        footer={
          <>
            <YhButton
              variant="secondary"
              fullWidth={false}
              disabled={leadSaving}
              onClick={() => setLeadOpen(false)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              fullWidth={false}
              loading={leadSaving}
              onClick={submitLead}
            >
              Save lead
            </YhButton>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#6B7280]">
            Enter mobile with country code (full 10 digits or last 4). Source is
            set to Channel Partner automatically.
          </p>
          {lockedOrgId ? (
            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-sm text-[#374151]">
              Organization is set from your login.
            </div>
          ) : (
            <YhMultiSelect
              label="Organizations"
              placeholder="Select organizations…"
              searchPlaceholder="Search organizations…"
              requiredMark
              showSelectAll
              maxVisibleChips={3}
              options={leadOrgOptions}
              value={leadForm.organizationIds}
              onChange={(values) =>
                setLeadForm((f) => ({
                  ...f,
                  organizationIds: values.map(String),
                  projectIds: [],
                }))
              }
            />
          )}
          <YhMultiSelect
            label="Projects"
            placeholder={
              leadSelectedOrgIds.length
                ? "Select projects…"
                : "Select organization first…"
            }
            searchPlaceholder="Search projects…"
            showSelectAll
            maxVisibleChips={3}
            disabled={!leadSelectedOrgIds.length}
            options={leadProjectOptions}
            value={leadForm.projectIds}
            onChange={(values) =>
              setLeadForm((f) => ({
                ...f,
                projectIds: values.map(String),
              }))
            }
          />
          <YhInput
            label="Lead name *"
            value={leadForm.leadName}
            onChange={(e: any) =>
              setLeadForm((f) => ({ ...f, leadName: e.target.value }))
            }
            placeholder="Customer name"
          />
          <PhoneWithCountryCode
            label="Mobile"
            required
            countryCode={leadForm.phoneCountryCode}
            phone={leadForm.leadPhone}
            phonePlaceholder="10-digit or last 4"
            onCountryChange={(code) =>
              setLeadForm((f) => ({ ...f, phoneCountryCode: code }))
            }
            onPhoneChange={(digits) =>
              setLeadForm((f) => ({ ...f, leadPhone: digits }))
            }
          />
          <YhInput
            label="Email"
            value={leadForm.leadEmail}
            onChange={(e: any) =>
              setLeadForm((f) => ({ ...f, leadEmail: e.target.value }))
            }
            placeholder="optional"
          />
          <YhSelect
            label="Stage *"
            placeholder="Select stage"
            searchPlaceholder="Search stages..."
            searchable
            showOptionIcon={false}
            value={leadForm.stage}
            groups={stageGroups}
            onChange={(e: any) =>
              setLeadForm((f) => ({
                ...f,
                stage: String(e?.target?.value || ""),
              }))
            }
          />
        </div>
      </YhModal>
      </div>

      <aside
        aria-hidden={!selectedPartner}
        className={`relative h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out ${
          selectedPartner
            ? "w-[280px] opacity-100 sm:w-[300px] xl:w-[340px]"
            : "pointer-events-none w-0 opacity-0"
        }`}
      >
        <div
          className={`relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition-transform duration-300 ease-in-out ${
            selectedPartner ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ width: "100%" }}
        >
          <button
            type="button"
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
            onClick={() => setSelectedPartner(null)}
            aria-label="Close partner details"
          >
            <CloseIcon width={"12px"} height={"12px"} />
          </button>
          <div className="h-full w-full overflow-auto p-4 pt-12 [scrollbar-width:thin]">
            {selectedPartner ? (
              <ChannelPartnerCallTray
                partner={selectedPartner}
                onOpenDashboard={() => {
                  const id = selectedPartner?._id;
                  if (id) {
                    history.push(`${urls.channelPartnersPath}/${id}`);
                  }
                }}
              />
            ) : null}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default ChannelPartnersPage;
